import { useMutation, useQueryClient } from "react-query";

import { unarchiveTarget } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_TARGETS, GET_TARGET_BY_ID } from "@constants/reactQueryKeys";

interface UnarchiveTargetPayload {
  targetId: number;
  /** Parent goal id — required for invalidating the target list query. */
  goalId: number;
}

/**
 * Restores an archived target (silent action — no confirmation dialog,
 * mirroring archive). The target returns to the active list with its session
 * history intact. Invalidates the target list and the target detail queries
 * so the UI reflects the restored state immediately.
 */
const useUnarchiveTarget = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: unarchiveTargetAction,
    isLoading: isUnarchivingTarget,
    ...rest
  } = useMutation(({ targetId }: UnarchiveTargetPayload) => unarchiveTarget(targetId), {
    onSuccess: (response, variables) => {
      showToast({ message: "Target unarchived" });
      queryClient.invalidateQueries([GET_TARGET_BY_ID, variables.targetId]);
      queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.goalId]);
      queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);
      onSuccess(response, variables);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || "Failed to unarchive target";
      showToast({ message: errorMessage, errorState: true });
      onError(error);
    },
    ...otherOptions,
  });

  return { unarchiveTargetAction, isUnarchivingTarget, ...rest };
};

export default useUnarchiveTarget;
